import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarPlus, Ban, Clock3, CheckCircle2, XCircle, FileText } from "lucide-react";
import { statusMeta, fmtDate, fmtDays, todayISO } from "@/pages/hr/leave/leaveUtils";

function ApplyDialog({ open, onClose, onDone }) {
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(todayISO());
  const [startHalf, setStartHalf] = useState("full");
  const [endHalf, setEndHalf] = useState("full");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const type = types.find((t) => t.id === typeId);
  const halfOk = !!type?.allow_half_day;
  const single = start === end;

  useEffect(() => {
    if (open) {
      api.get("/hr/leave/types/active").then((r) => setTypes(r.data)).catch(() => {});
      setTypeId(""); setStart(todayISO()); setEnd(todayISO()); setStartHalf("full");
      setEndHalf("full"); setReason(""); setFile(null); setCalc(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !typeId || !start || !end) { setCalc(null); return; }
    const sh = halfOk && startHalf !== "full" ? startHalf : null;
    const eh = halfOk && !single && endHalf !== "full" ? endHalf : null;
    const h = setTimeout(() => {
      api.post("/hr/leave/me/calc", { leave_type_id: typeId, start_date: start, end_date: end, start_half: sh, end_half: eh })
        .then((r) => setCalc(r.data)).catch(() => setCalc(null));
    }, 300);
    return () => clearTimeout(h);
  }, [open, typeId, start, end, startHalf, endHalf, halfOk, single]);

  const submit = async () => {
    if (!typeId) return toast.error("Select a leave type");
    if (type?.requires_attachment && !file) return toast.error("This leave type requires an attachment");
    if (calc?.blocked) return toast.error("Insufficient balance — this leave type does not allow a negative balance");
    const sh = halfOk && startHalf !== "full" ? startHalf : null;
    const eh = halfOk && !single && endHalf !== "full" ? endHalf : null;
    setBusy(true);
    try {
      const { data: app } = await api.post("/hr/leave/me/apply", {
        leave_type_id: typeId, start_date: start, end_date: end, start_half: sh, end_half: eh, reason });
      if (file) { const fd = new FormData(); fd.append("file", file); await api.post(`/hr/leave/applications/${app.id}/attachment`, fd); }
      toast.success("Leave application submitted"); onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="leave-apply-dialog">
        <DialogHeader><DialogTitle>Apply for Leave</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Leave Type</Label>
            <Select value={typeId} onValueChange={setTypeId}>
              <SelectTrigger data-testid="leave-apply-type"><SelectValue placeholder="Select leave type" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} · {t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Start Date</Label><Input type="date" value={start} data-testid="leave-apply-start" onChange={(e) => { setStart(e.target.value); if (e.target.value > end) setEnd(e.target.value); }} className="h-9" /></div>
            <div><Label className="text-xs">End Date</Label><Input type="date" value={end} min={start} data-testid="leave-apply-end" onChange={(e) => setEnd(e.target.value)} className="h-9" /></div>
          </div>
          {halfOk && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{single ? "Day Portion" : "First Day"}</Label>
                <Select value={startHalf} onValueChange={setStartHalf}>
                  <SelectTrigger className="h-9" data-testid="leave-apply-start-half"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Day</SelectItem>
                    <SelectItem value="am">Half Day (AM)</SelectItem>
                    <SelectItem value="pm">Half Day (PM)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!single && (
                <div>
                  <Label className="text-xs">Last Day</Label>
                  <Select value={endHalf} onValueChange={setEndHalf}>
                    <SelectTrigger className="h-9" data-testid="leave-apply-end-half"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full Day</SelectItem>
                      <SelectItem value="am">Half Day (AM)</SelectItem>
                      <SelectItem value="pm">Half Day (PM)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <div><Label className="text-xs">Reason / Remarks</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="leave-apply-reason" placeholder="Reason for leave" /></div>
          <div>
            <Label className="text-xs flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Attachment {type?.requires_attachment ? <span className="text-rose-600">(required)</span> : <span className="text-slate-400">(PDF/JPG/PNG)</span>}</Label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" data-testid="leave-apply-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm mt-1" />
          </div>

          {calc && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="leave-apply-calc">
              <div className="grid grid-cols-3 gap-2 text-center text-sm mb-2">
                <div><div className="text-[11px] text-slate-400">Current</div><div className="font-semibold" data-testid="calc-current">{fmtDays(calc.current_balance)}</div></div>
                <div><div className="text-[11px] text-slate-400">Requested</div><div className="font-semibold text-sky-700" data-testid="calc-requested">{fmtDays(calc.requested_days)}</div></div>
                <div><div className="text-[11px] text-slate-400">After</div><div className={`font-semibold ${calc.balance_after < 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid="calc-after">{fmtDays(calc.balance_after)}</div></div>
              </div>
              {calc.blocked && <div className="text-xs text-rose-600" data-testid="leave-apply-blocked">Insufficient leave balance. This leave type does not allow negative balance.</div>}
              {calc.insufficient && !calc.blocked && <div className="text-xs text-amber-600" data-testid="leave-apply-negative">Negative balance — this will be flagged to your approver.</div>}
              <div className="mt-2 max-h-36 overflow-y-auto border-t border-slate-200 pt-2">
                {calc.breakdown.map((b) => (
                  <div key={b.date} className="flex items-center justify-between text-xs py-0.5">
                    <span className="font-mono">{b.date} <span className="uppercase text-slate-400">{b.weekday}</span></span>
                    {b.scheduled ? <span className="text-emerald-700">{fmtDays(b.counted)} day{b.half_period ? ` (${b.half_period.toUpperCase()})` : ""}</span>
                      : <span className="text-slate-400">{b.reason} · 0</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !typeId || calc?.blocked} data-testid="leave-apply-submit" className="bg-sky-600 hover:bg-sky-700">Submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ app, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/hr/leave/me/applications/${app.id}/cancel-request`, { reason }); toast.success("Cancellation requested — awaiting admin confirmation"); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!app} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="leave-cancel-dialog">
        <DialogHeader><DialogTitle>Request Leave Cancellation</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{app?.leave_type_name} · {fmtDate(app?.start_date)} – {fmtDate(app?.end_date)} ({fmtDays(app?.requested_days)} days)</p>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for cancellation (optional)" data-testid="leave-cancel-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={submit} disabled={busy} data-testid="leave-cancel-submit" className="bg-violet-600 hover:bg-violet-700">Request Cancellation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const AppRow = ({ a, onCancel }) => {
  const m = statusMeta(a.status);
  const future = a.start_date >= todayISO();
  return (
    <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-50 last:border-0" data-testid={`myleave-app-${a.id}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium flex items-center gap-2">{a.leave_type_name}
          {a.half_day && <Badge variant="outline" className="text-[10px] text-sky-700 border-sky-300">½</Badge>}
          {a.has_attachment && <FileText className="w-3.5 h-3.5 text-slate-400" />}</div>
        <div className="text-xs text-slate-500 font-mono">{fmtDate(a.start_date)} – {fmtDate(a.end_date)} · {fmtDays(a.requested_days)} day(s)</div>
        {a.status === "rejected" && a.rejection_reason && <div className="text-xs text-rose-500 mt-0.5">Rejected: {a.rejection_reason}</div>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Badge variant="outline" className={m.cls}>{m.label}</Badge>
        {a.status === "approved" && future && (
          <Button size="sm" variant="outline" className="h-7 text-xs border-violet-300 text-violet-700" data-testid={`myleave-cancel-${a.id}`} onClick={() => onCancel(a)}><Ban className="w-3 h-3 mr-1" />Cancel</Button>
        )}
      </div>
    </div>
  );
};

export default function MyLeave() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [cancelFor, setCancelFor] = useState(null);

  const load = () => api.get("/hr/leave/me/summary").then((r) => { setData(r.data); setErr(null); })
    .catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  if (err) return <div className="p-8 text-center text-slate-500" data-testid="myleave-error">{err}</div>;
  if (!data) return <div className="p-8 text-center text-slate-400" data-testid="myleave-loading">Loading…</div>;

  const apps = data.applications || [];
  const active = apps.filter((a) => ["pending", "draft", "cancel_requested"].includes(a.status));
  const approved = apps.filter((a) => a.status === "approved");
  const history = apps.filter((a) => ["rejected", "cancelled"].includes(a.status));

  return (
    <div data-testid="myleave-page" className="max-w-2xl mx-auto">
      <header className="mb-5 flex items-end justify-between gap-3">
        <div>
          <div className="caption">Leave</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">My Leave</h1>
          <p className="text-slate-500 mt-1 text-sm font-mono">{data.employee_id} · {data.full_name}</p>
        </div>
        <Button onClick={() => setShowApply(true)} data-testid="myleave-apply-btn" className="bg-sky-600 hover:bg-sky-700"><CalendarPlus className="w-4 h-4 mr-1.5" />Apply Leave</Button>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6" data-testid="myleave-balances">
        {data.balances.map((b) => (
          <div key={b.leave_type_id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`myleave-balance-${b.code}`}>
            <div className="text-xs text-slate-400 uppercase tracking-wide">{b.code}</div>
            <div className="text-sm font-medium text-slate-700 truncate">{b.name}</div>
            <div className={`text-2xl font-extrabold mt-1 ${b.balance < 0 ? "text-rose-600" : "text-slate-900"}`}>{fmtDays(b.balance)}</div>
          </div>
        ))}
      </div>

      <Section title="Pending & Active" icon={<Clock3 className="w-4 h-4 text-amber-500" />} items={active} empty="No pending applications" onCancel={setCancelFor} testid="myleave-pending" />
      <Section title="Approved" icon={<CheckCircle2 className="w-4 h-4 text-emerald-500" />} items={approved} empty="No approved leave" onCancel={setCancelFor} testid="myleave-approved" />
      <Section title="History" icon={<XCircle className="w-4 h-4 text-slate-400" />} items={history} empty="No past records" onCancel={setCancelFor} testid="myleave-history" />

      <ApplyDialog open={showApply} onClose={() => setShowApply(false)} onDone={load} />
      <CancelDialog app={cancelFor} onClose={() => setCancelFor(null)} onDone={load} />
    </div>
  );
}

const Section = ({ title, icon, items, empty, onCancel, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-4" data-testid={testid}>
    <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold flex items-center gap-1.5">{icon}{title} <span className="text-slate-400 font-normal">({items.length})</span></div>
    {items.length === 0 ? <div className="px-4 py-5 text-center text-sm text-slate-400">{empty}</div>
      : items.map((a) => <AppRow key={a.id} a={a} onCancel={onCancel} />)}
  </div>
);
